import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { authService, creditsService, userService } from '../services/api';
import {
  IconChevronLeft,
  IconChevronRight,
  IconComplaints,
  IconDailyGuests,
  IconDashboard,
  IconDocuments,
  IconDues,
  IconExpenses,
  IconHostels,
  IconInvoices,
  IconLeads,
  IconMenu,
  IconOnboarding,
  IconReports,
  IconRooms,
  IconSettings,
  IconCredits,
  IconSubscriptions,
  IconTenants,
  IconUsers,
} from './NavIcons';
import { resolveMediaUrl } from '../utils/mediaUrl';
import './Layout.css';

type NavItem = {
  to: string;
  label: string;
  icon: React.ReactNode;
};

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const {
    user,
    hostel,
    hostelScopeKey,
    logout,
    isSuperAdmin,
    isHostelAdmin,
    isStaff,
    isTenant,
    isGuest,
    isResident,
    switchHostel,
  } = useAuth();
  const showHostelProfileInNav = isHostelAdmin() && !isSuperAdmin();
  const showTenantAccountInNav = isResident();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [hostelSwitchBusy, setHostelSwitchBusy] = useState(false);
  const [switchableHostels, setSwitchableHostels] = useState<Array<{ id: number; name: string }>>([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [hostelMenuOpen, setHostelMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [creditsPageEnabled, setCreditsPageEnabled] = useState(true);
  const [emailCreditsVisible, setEmailCreditsVisible] = useState(true);
  const [whatsappCreditsVisible, setWhatsappCreditsVisible] = useState(true);
  const [brandLogoErr, setBrandLogoErr] = useState(false);
  const [profileLogoErr, setProfileLogoErr] = useState(false);
  const navActionsRef = useRef<HTMLDivElement>(null);

  const canOperateProperty = useMemo(() => {
    if (isSuperAdmin() || isResident()) return false;
    const ut = String(user?.userType || '').toUpperCase();
    return ut === 'HOSTEL_ADMIN' || ut === 'STAFF' || isHostelAdmin() || isStaff();
  }, [user?.userType, isSuperAdmin, isResident, isHostelAdmin, isStaff]);

  const assignedHostels = useMemo(() => {
    const eligible = (list: { id: number; name: string; isDeleted?: boolean; status?: string }[]) =>
      list.filter((h) => {
        if (h.isDeleted === true) return false;
        const st = String(h.status || 'ACTIVE').toUpperCase();
        return st === 'ACTIVE';
      });

    const fromList = eligible(user?.hostels ?? []);
    const byId = new Map<number, { id: number; name: string }>();
    for (const h of fromList) {
      if (h.id != null) byId.set(h.id, { id: h.id, name: h.name || `Hostel ${h.id}` });
    }

    const ids = user?.hostelIds ?? [];
    for (const rawId of ids) {
      const id = Number(rawId);
      if (!Number.isFinite(id) || id <= 0 || byId.has(id)) continue;
      byId.set(id, {
        id,
        name: id === hostel?.id ? hostel?.name ?? `Hostel ${id}` : `Hostel ${id}`,
      });
    }

    if (byId.size === 0 && hostel?.id) {
      byId.set(hostel.id, { id: hostel.id, name: hostel.name || `Hostel ${hostel.id}` });
    }

    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [user?.hostels, user?.hostelIds, hostel?.id, hostel?.name]);

  const propertyHostels =
    switchableHostels.length > 0 ? switchableHostels : assignedHostels;

  const showHostelSwitcher = canOperateProperty && propertyHostels.length > 0;
  const canSwitchProperty = propertyHostels.length > 1;

  useEffect(() => {
    if (!canOperateProperty || !user?.id) {
      setSwitchableHostels([]);
      return;
    }
    let cancelled = false;
    void authService
      .getAccessibleHostels()
      .then((data) => {
        if (cancelled) return;
        const list = (data.hostels ?? [])
          .filter((h) => h.isDeleted !== true)
          .map((h) => ({
            id: h.id,
            name: h.name || `Hostel ${h.id}`,
          }));
        setSwitchableHostels(list);
      })
      .catch(() => {
        if (!cancelled) setSwitchableHostels(assignedHostels);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, hostelScopeKey, canOperateProperty]);

  useEffect(() => {
    if (!isHostelAdmin() || isSuperAdmin()) {
      setCreditsPageEnabled(true);
      setEmailCreditsVisible(true);
      setWhatsappCreditsVisible(true);
      return;
    }
    let cancelled = false;
    void creditsService
      .getCreditShop()
      .then((shop) => {
        if (!cancelled) {
          setCreditsPageEnabled(shop.creditsPageEnabled !== false);
          setEmailCreditsVisible(shop.emailCreditsVisible !== false);
          setWhatsappCreditsVisible(shop.whatsappCreditsVisible !== false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCreditsPageEnabled(true);
          setEmailCreditsVisible(true);
          setWhatsappCreditsVisible(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hostelScopeKey, isHostelAdmin, isSuperAdmin]);

  const handleLogout = () => {
    setProfileOpen(false);
    setChangePasswordOpen(false);
    logout();
    navigate('/login');
  };

  const openChangePassword = () => {
    setProfileOpen(false);
    setHostelMenuOpen(false);
    setNewPassword('');
    setConfirmPassword('');
    setChangePasswordOpen(true);
  };

  const closeChangePassword = () => {
    if (passwordSaving) return;
    setChangePasswordOpen(false);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    const pwd = newPassword.trim();
    const confirm = confirmPassword.trim();
    if (pwd.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (pwd !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setPasswordSaving(true);
    try {
      await userService.changePassword(user.id, pwd);
      toast.success('Password updated successfully');
      closeChangePassword();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleHostelSelect = async (hostelId: number) => {
    if (!Number.isFinite(hostelId) || hostelId === hostel?.id) {
      setHostelMenuOpen(false);
      return;
    }
    setHostelSwitchBusy(true);
    setHostelMenuOpen(false);
    try {
      await switchHostel(hostelId);
      const switched =
        propertyHostels.find((h) => h.id === hostelId) || user?.hostels?.find((h) => h.id === hostelId);
      const label = switched?.name || `Hostel ${hostelId}`;
      toast.success(`Switched to ${label}. Refreshing data…`);

      const path = location.pathname;
      if (/^\/tenants\/(view|edit)\/\d+/.test(path) || path === '/tenants/create') {
        navigate('/tenants', { replace: true });
      } else if (path.startsWith('/create-invoice')) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate(path, { replace: true });
      }
    } catch (err: unknown) {
      console.error(err);
      const e = err as { response?: { data?: { message?: string } } };
      window.alert(e?.response?.data?.message || 'Could not switch hostel. Try again.');
    } finally {
      setHostelSwitchBusy(false);
    }
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!navActionsRef.current?.contains(e.target as Node)) {
        setProfileOpen(false);
        setHostelMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    setProfileOpen(false);
    setHostelMenuOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => {
      setIsMobile(mq.matches);
      if (!mq.matches) setMobileMenuOpen(false);
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const isActive = (path: string) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`));

  const closeMobileMenu = () => {
    if (isMobile) setMobileMenuOpen(false);
  };

  const renderNavLink = (item: NavItem) => (
    <Link
      key={item.to}
      to={item.to}
      className={`sidebar-nav-link${isActive(item.to) ? ' is-active' : ''}`}
      data-label={item.label}
      title={sidebarOpen || isMobile ? undefined : item.label}
      onClick={closeMobileMenu}
    >
      <span className="sidebar-nav-link__icon">{item.icon}</span>
      {sidebarOpen || isMobile ? <span className="sidebar-nav-link__label">{item.label}</span> : null}
    </Link>
  );

  const hostelAdminPrimary: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', icon: <IconDashboard /> },
    { to: '/tenants', label: 'Tenants', icon: <IconTenants /> },
    { to: '/rooms-beds', label: 'Rooms & Beds', icon: <IconRooms /> },
    { to: '/tenant-onboarding', label: 'Onboarding', icon: <IconOnboarding /> },
    { to: '/invoices', label: 'Invoices', icon: <IconInvoices /> },
    { to: '/dues', label: 'Dues', icon: <IconDues /> },
    { to: '/reports', label: 'Reports', icon: <IconReports /> },
    { to: '/daily-guests', label: 'Daily guests', icon: <IconDailyGuests /> },
    { to: '/leads', label: 'Leads', icon: <IconLeads /> },
    { to: '/expenses', label: 'Expenses', icon: <IconExpenses /> },
    { to: '/complaints', label: 'Complaints', icon: <IconComplaints /> },
  ];

  const hostelAdminSecondary: NavItem[] = [
    { to: '/credits', label: 'Credits', icon: <IconCredits /> },
    { to: '/settings', label: 'Settings', icon: <IconSettings /> },
    { to: '/documents', label: 'Documents', icon: <IconDocuments /> },
  ];

  const creditsNavVisible =
    creditsPageEnabled && (emailCreditsVisible || whatsappCreditsVisible);

  const hostelAdminSecondaryVisible = useMemo(
    () => hostelAdminSecondary.filter((item) => item.to !== '/credits' || creditsNavVisible),
    [creditsNavVisible],
  );

  const superAdminNav: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', icon: <IconDashboard /> },
    { to: '/hostels', label: 'Hostels', icon: <IconHostels /> },
    { to: '/users', label: 'Super admins', icon: <IconUsers /> },
    { to: '/subscription-admin', label: 'Subscriptions', icon: <IconSubscriptions /> },
    { to: '/settings', label: 'Settings', icon: <IconSettings /> },
  ];

  const staffNav: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', icon: <IconDashboard /> },
    { to: '/complaints', label: 'Complaints', icon: <IconComplaints /> },
    { to: '/my-invoices', label: 'My Invoices', icon: <IconInvoices /> },
    { to: '/documents', label: 'Documents', icon: <IconDocuments /> },
  ];

  const residentNav: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', icon: <IconDashboard /> },
    ...(isGuest() ? [{ to: '/my-stay', label: 'Pay & stay', icon: <IconInvoices /> }] : []),
    { to: '/my-invoices', label: 'Transactions', icon: <IconInvoices /> },
    { to: '/my-account', label: 'My Profile', icon: <IconTenants /> },
    { to: '/my-hostel', label: 'Hostel', icon: <IconHostels /> },
    { to: '/complaints', label: 'Complaints', icon: <IconComplaints /> },
  ];

  const mobileBottomNav: NavItem[] = isSuperAdmin()
    ? [
        { to: '/dashboard', label: 'Home', icon: <IconDashboard /> },
        { to: '/hostels', label: 'Hostels', icon: <IconHostels /> },
        { to: '/users', label: 'Admins', icon: <IconUsers /> },
        { to: '/settings', label: 'Settings', icon: <IconSettings /> },
      ]
    : isHostelAdmin() && !isSuperAdmin()
      ? [
          { to: '/dashboard', label: 'Home', icon: <IconDashboard /> },
          { to: '/tenants', label: 'Tenants', icon: <IconTenants /> },
          { to: '/invoices', label: 'Invoices', icon: <IconInvoices /> },
          { to: '/reports', label: 'Reports', icon: <IconReports /> },
        ]
      : isResident()
        ? isGuest()
          ? [
              { to: '/dashboard', label: 'Home', icon: <IconDashboard /> },
              { to: '/my-stay', label: 'Pay', icon: <IconInvoices /> },
              { to: '/my-invoices', label: 'History', icon: <IconDues /> },
              { to: '/my-account', label: 'Profile', icon: <IconTenants /> },
            ]
          : [
              { to: '/dashboard', label: 'Home', icon: <IconDashboard /> },
              { to: '/my-invoices', label: 'Bills', icon: <IconInvoices /> },
              { to: '/complaints', label: 'Issues', icon: <IconComplaints /> },
              { to: '/my-account', label: 'Profile', icon: <IconTenants /> },
            ]
        : [
            { to: '/dashboard', label: 'Home', icon: <IconDashboard /> },
            { to: '/complaints', label: 'Issues', icon: <IconComplaints /> },
            { to: '/my-invoices', label: 'Bills', icon: <IconInvoices /> },
          ];

  const sidebarClass = [
    'sidebar',
    !isMobile && sidebarOpen ? 'sidebar--open' : '',
    !isMobile && !sidebarOpen ? 'sidebar--collapsed' : '',
    isMobile && mobileMenuOpen ? 'sidebar--mobile-open' : '',
    isMobile ? 'sidebar--mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const profileLabel = user?.username || 'Account';
  const logoUrl = resolveMediaUrl(hostel?.logo);
  const profileSource = showHostelProfileInNav
    ? hostel?.name || user?.username || 'H'
    : user?.username || 'U';
  const profileInitial = profileSource.charAt(0).toUpperCase();
  const displayBrandName = hostel?.name || 'Hostel';
  const showBrandLogo = Boolean(logoUrl) && !brandLogoErr;
  const showProfileLogo = showHostelProfileInNav && Boolean(logoUrl) && !profileLogoErr;

  useEffect(() => {
    setBrandLogoErr(false);
    setProfileLogoErr(false);
  }, [hostel?.id, hostel?.logo]);

  return (
    <div className="layout">
      {isMobile && mobileMenuOpen ? (
        <button
          type="button"
          className="layout-sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setMobileMenuOpen(false)}
        />
      ) : null}
      <div className="layout-shell">
        <aside className={sidebarClass}>
          <div className="sidebar-header">
            <div className="sidebar-brand">
              {showBrandLogo ? (
                <img
                  src={logoUrl}
                  alt=""
                  className="sidebar-brand-logo"
                  onError={() => setBrandLogoErr(true)}
                />
              ) : (
                <span className="sidebar-brand-logo sidebar-brand-logo--placeholder" aria-hidden>
                  {profileInitial}
                </span>
              )}
              {sidebarOpen || isMobile ? <span className="sidebar-brand-name">{displayBrandName}</span> : null}
            </div>
            <button
              type="button"
              className="sidebar-collapse-btn"
              onClick={() => setSidebarOpen((o) => !o)}
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              aria-expanded={sidebarOpen}
            >
              {sidebarOpen ? <IconChevronLeft /> : <IconChevronRight />}
            </button>
          </div>

          <nav className="sidebar-nav">
            {isSuperAdmin() && superAdminNav.map(renderNavLink)}

            {isHostelAdmin() && !isSuperAdmin() && (
              <>
                {hostelAdminPrimary.map(renderNavLink)}
                <div className="sidebar-nav-divider" role="separator" />
                {hostelAdminSecondaryVisible.map(renderNavLink)}
              </>
            )}

            {isStaff() && !isHostelAdmin() && !isSuperAdmin() && !isResident() && staffNav.map(renderNavLink)}

            {isResident() && residentNav.map(renderNavLink)}
          </nav>
        </aside>

        <div className="layout-main">
          <header className="app-topbar">
            {isMobile ? (
              <button
                type="button"
                className="layout-menu-btn"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((o) => !o)}
              >
                <IconMenu />
              </button>
            ) : null}
            {!sidebarOpen || isMobile ? (
              <div className="app-topbar-brand">
                {showBrandLogo ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className="app-topbar-brand-logo"
                    onError={() => setBrandLogoErr(true)}
                  />
                ) : (
                  <span className="app-topbar-brand-logo app-topbar-brand-logo--placeholder" aria-hidden>
                    {profileInitial}
                  </span>
                )}
                <span className="app-topbar-hostel">{displayBrandName}</span>
              </div>
            ) : (
              <span className="app-topbar-spacer" />
            )}
            <div className="navbar-actions" ref={navActionsRef}>
              {showHostelSwitcher ? (
                <div className="navbar-dropdown">
                  <button
                    type="button"
                    className={`navbar-dropdown-trigger navbar-hostel-trigger${hostelMenuOpen ? ' is-open' : ''}`}
                    onClick={() => {
                      setHostelMenuOpen((o) => !o);
                      setProfileOpen(false);
                    }}
                    disabled={hostelSwitchBusy}
                    aria-expanded={hostelMenuOpen}
                    aria-haspopup="listbox"
                  >
                    <span className="navbar-hostel-trigger-label">Property</span>
                    <span className="navbar-hostel-trigger-value">{hostel?.name || 'Select property'}</span>
                    <span className="navbar-dropdown-chevron" aria-hidden>
                      ▾
                    </span>
                  </button>
                  {hostelMenuOpen ? (
                    <ul className="navbar-dropdown-menu" role="listbox" aria-label="Switch property">
                      {!canSwitchProperty ? (
                        <li className="navbar-dropdown-hint" style={{ padding: '0.5rem 0.85rem', fontSize: '0.8rem', color: '#64748b' }}>
                          Only one property assigned. Ask your platform admin to map more hostels to your account.
                        </li>
                      ) : null}
                      {propertyHostels.map((h) => (
                        <li key={h.id} role="option" aria-selected={h.id === hostel?.id}>
                          <button
                            type="button"
                            className={`navbar-dropdown-item${h.id === hostel?.id ? ' is-active' : ''}`}
                            disabled={!canSwitchProperty && h.id !== hostel?.id}
                            onClick={() => void handleHostelSelect(h.id)}
                          >
                            {h.name}
                            {h.id === hostel?.id ? <span className="navbar-dropdown-check">✓</span> : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              <div className="navbar-dropdown navbar-profile-dropdown">
                <button
                  type="button"
                  className={`navbar-profile-btn${profileOpen ? ' is-open' : ''}`}
                  onClick={() => {
                    setProfileOpen((o) => !o);
                    setHostelMenuOpen(false);
                  }}
                  aria-expanded={profileOpen}
                  aria-haspopup="menu"
                  aria-label="Account menu"
                >
                  {showProfileLogo ? (
                    <img
                      src={logoUrl}
                      alt=""
                      className="navbar-profile-logo"
                      onError={() => setProfileLogoErr(true)}
                    />
                  ) : (
                    <span className="navbar-profile-logo navbar-profile-logo--placeholder" aria-hidden>
                      {profileInitial}
                    </span>
                  )}
                  <span className="navbar-profile-chevron" aria-hidden>
                    ▾
                  </span>
                </button>
                {profileOpen ? (
                  <div className="navbar-dropdown-menu navbar-profile-menu" role="menu">
                    <div className="navbar-profile-menu-head">
                      <p className="navbar-profile-menu-name">{profileLabel}</p>
                      {hostel?.name ? (
                        <p className="navbar-profile-menu-hostel">{hostel.name}</p>
                      ) : null}
                    </div>
                    {showHostelProfileInNav ? (
                      <button
                        type="button"
                        role="menuitem"
                        className="navbar-dropdown-item"
                        onClick={() => {
                          setProfileOpen(false);
                          navigate('/profile');
                        }}
                      >
                        Hostel profile
                      </button>
                    ) : null}
                    {showTenantAccountInNav ? (
                      <button
                        type="button"
                        role="menuitem"
                        className="navbar-dropdown-item"
                        onClick={() => {
                          setProfileOpen(false);
                          navigate('/my-account');
                        }}
                      >
                        My profile
                      </button>
                    ) : null}
                    <button type="button" role="menuitem" className="navbar-dropdown-item" onClick={openChangePassword}>
                      Change password
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="navbar-dropdown-item navbar-dropdown-item--danger"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </header>

          <main className="main-content" key={hostelScopeKey}>
            {children}
          </main>
        </div>
      </div>

      {isMobile ? (
        <nav className="layout-bottom-nav" aria-label="Quick navigation">
          {mobileBottomNav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`layout-bottom-nav__item${isActive(item.to) ? ' is-active' : ''}`}
              onClick={closeMobileMenu}
            >
              <span className="layout-bottom-nav__icon">{item.icon}</span>
              <span className="layout-bottom-nav__label">{item.label}</span>
            </Link>
          ))}
          <button
            type="button"
            className={`layout-bottom-nav__item${mobileMenuOpen ? ' is-active' : ''}`}
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-label="More menu"
          >
            <span className="layout-bottom-nav__icon">
              <IconMenu />
            </span>
            <span className="layout-bottom-nav__label">More</span>
          </button>
        </nav>
      ) : null}

      {changePasswordOpen ? (
        <div className="layout-pw-overlay" role="presentation" onClick={closeChangePassword}>
          <div
            className="layout-pw-modal"
            role="dialog"
            aria-labelledby="layout-pw-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="layout-pw-title" className="layout-pw-title">
              Change password
            </h2>
            <p className="layout-pw-hint">Enter and confirm your new password (min 6 characters).</p>
            <form onSubmit={handleChangePasswordSubmit}>
              <label className="layout-pw-label">
                <span>New password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  disabled={passwordSaving}
                  required
                  minLength={6}
                />
              </label>
              <label className="layout-pw-label">
                <span>Confirm password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  disabled={passwordSaving}
                  required
                  minLength={6}
                />
              </label>
              <div className="layout-pw-actions">
                <button
                  type="button"
                  className="layout-pw-btn layout-pw-btn--ghost"
                  disabled={passwordSaving}
                  onClick={closeChangePassword}
                >
                  Cancel
                </button>
                <button type="submit" className="layout-pw-btn layout-pw-btn--primary" disabled={passwordSaving}>
                  {passwordSaving ? 'Saving…' : 'Update password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Layout;
